import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, Info, ChevronDown, ChevronUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNum = (v, unit) => {
  if (v == null) return "-";
  if (unit === 'EUR') return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  if (unit === '%') return `${v}%`;
  if (unit === 'x') return `${v}x`;
  if (unit === 'mesi') return `${v} mesi`;
  return v;
};

const statusColors = {
  good: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500', stroke: '#059669', fill: '#d1fae5' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500', stroke: '#d97706', fill: '#fef3c7' },
  danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500', stroke: '#dc2626', fill: '#fee2e2' },
  critical: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-200 text-red-900 border-red-400', dot: 'bg-red-600', stroke: '#991b1b', fill: '#fecaca' },
};

const statusLabels = { good: 'Tutto bene', warning: 'Da tenere d\'occhio', danger: 'Serve attenzione', critical: 'Agisci subito' };

const MONTHS_IT = ['', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const TREND_KEY_MAP = {
  ros: 'ros', ebitda_margin: 'ebitda_pct', mdc_pct: 'mdc_pct',
  var_costs_pct: 'var_costs_pct', labor_pct: 'labor_pct', fix_costs_pct: 'fix_costs_pct',
  bep_coverage: 'bep_coverage',
};

// Trend chart per indicator
const TrendChart = ({ data, dataKey, color, fillColor, unit, thresholdWarning, thresholdDanger, higherIsBetter }) => {
  if (!data || data.length < 2) return <p className="text-[11px] text-slate-400 italic py-4 text-center">Non ci sono abbastanza dati per mostrare l'andamento.</p>;

  const values = data.map(d => d[dataKey]).filter(v => v != null);
  if (values.length < 2) return null;
  const first = values[0];
  const last = values[values.length - 1];
  const diff = last - first;
  const improved = higherIsBetter ? diff > 0 : diff < 0;

  const chartData = data.map(d => ({
    ...d,
    label: `${MONTHS_IT[d.month]} ${String(d.year).slice(2)}`,
  }));

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-xs font-medium text-slate-500">Andamento ultimi 12 mesi</span>
        <span className={`text-xs font-bold ${improved ? 'text-emerald-600' : Math.abs(diff) < 0.3 ? 'text-slate-400' : 'text-red-600'}`}>
          {improved ? '+' : ''}{diff.toFixed(1)}{unit === '%' ? ' pp' : unit === 'x' ? 'x' : unit === 'EUR' ? ' EUR' : ''} rispetto a 12 mesi fa
        </span>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} domain={['auto', 'auto']}
            tickFormatter={(v) => unit === 'EUR' ? `${(v/1000).toFixed(0)}k` : `${v}`} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
            formatter={(v) => [formatNum(v, unit), 'Valore']}
            labelFormatter={(l) => l}
          />
          {thresholdWarning != null && (
            <ReferenceLine y={thresholdWarning} stroke="#d97706" strokeDasharray="4 4" strokeWidth={1} />
          )}
          {thresholdDanger != null && (
            <ReferenceLine y={thresholdDanger} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={1} />
          )}
          <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#grad-${dataKey})`} strokeWidth={2.5}
            dot={{ r: 3, fill: color, stroke: '#fff', strokeWidth: 2 }}
            activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const IndicatorCard = ({ indicator, trendData }) => {
  const [expanded, setExpanded] = useState(false);
  const s = statusColors[indicator.status] || statusColors.warning;
  const trend = indicator.prev_value != null ? indicator.value - indicator.prev_value : null;
  const TrendIcon = trend == null || Math.abs(trend) < 0.1 ? Minus : (
    indicator.higher_is_better ? (trend > 0 ? TrendingUp : TrendingDown) : (trend < 0 ? TrendingUp : TrendingDown)
  );
  const trendGood = trend == null ? null : (indicator.higher_is_better ? trend > 0 : trend < 0);
  const trendKey = TREND_KEY_MAP[indicator.id];

  return (
    <Card className={`${s.bg} ${s.border} border-2 transition-all hover:shadow-lg`} data-testid={`kpi-${indicator.id}`}>
      <CardContent className="pt-4 pb-3">
        {/* Header: status + value */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${s.dot} animate-pulse`} />
              <Badge className={`${s.badge} border text-[10px] px-1.5`}>{statusLabels[indicator.status]}</Badge>
            </div>
            <h4 className="font-bold text-sm leading-tight">{indicator.name}</h4>
            {indicator.technical_name && (
              <p className="text-[10px] text-slate-400 mt-0.5">{indicator.technical_name}</p>
            )}
          </div>
          <div className="text-right ml-3">
            <div className={`text-3xl font-black tabular-nums tracking-tight ${s.text}`}>
              {formatNum(indicator.value, indicator.unit)}
            </div>
            {trend != null && (
              <div className={`flex items-center justify-end gap-0.5 text-xs mt-0.5 ${trendGood ? 'text-emerald-600' : trendGood === false ? 'text-red-500' : 'text-slate-400'}`}>
                <TrendIcon className="h-3.5 w-3.5" />
                <span className="font-semibold">{trend > 0 ? '+' : ''}{trend.toFixed(1)}</span>
                <span className="text-slate-400 ml-0.5">vs mese prec.</span>
              </div>
            )}
          </div>
        </div>

        {/* Benchmark comparison */}
        {indicator.benchmark_avg != null && (
          <div className="flex items-center gap-3 text-[11px] bg-white/60 rounded px-2 py-1.5 mb-2 border border-slate-200/50">
            <span className="text-slate-500">Media settore: <strong className="text-slate-700">{indicator.benchmark_avg}{indicator.unit === '%' ? '%' : ''}</strong></span>
            {indicator.benchmark_best && <span className="text-slate-500">Migliori: <strong className="text-slate-700">{indicator.benchmark_best}{indicator.unit === '%' ? '%' : ''}</strong></span>}
          </div>
        )}

        {/* Practical explanation - always visible */}
        <div className="bg-white/70 rounded-lg px-3 py-2.5 border border-slate-200/50 mb-1">
          <p className="text-[12px] leading-[1.6] text-slate-700">{indicator.explanation}</p>
        </div>

        {/* Suggestion for danger/critical */}
        {indicator.suggestion && (
          <div className={`mt-2 p-2.5 rounded-lg ${indicator.status === 'critical' ? 'bg-red-100 border-red-300' : 'bg-amber-50 border-amber-200'} border flex gap-2`}>
            <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${indicator.status === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5 text-slate-500">Cosa fare</p>
              <p className={`text-[12px] leading-relaxed ${indicator.status === 'critical' ? 'text-red-800' : 'text-amber-800'}`}>{indicator.suggestion}</p>
            </div>
          </div>
        )}

        {/* Trend chart toggle */}
        {trendKey && trendData && trendData.length > 1 && (
          <div className="mt-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-700 transition-colors w-full justify-center py-1 rounded hover:bg-white/50"
              data-testid={`kpi-${indicator.id}-trend-toggle`}
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {expanded ? 'Nascondi andamento' : 'Mostra andamento 12 mesi'}
            </button>
            {expanded && (
              <TrendChart
                data={trendData}
                dataKey={trendKey}
                color={s.stroke}
                fillColor={s.fill}
                unit={indicator.unit}
                thresholdWarning={indicator.threshold_warning}
                thresholdDanger={indicator.threshold_danger}
                higherIsBetter={indicator.higher_is_better}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const KPIDashboard = ({ clientId, year, month }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/kpi`, { params: { year, month } });
        setData(res.data);
      } catch (e) {
        if (e.response?.status !== 404) toast.error("Errore nel caricamento indicatori");
        setData(null);
      } finally { setLoading(false); }
    };
    if (clientId && year && month) fetch();
  }, [clientId, year, month]);

  if (loading) return <div className="py-12 text-center text-muted-foreground">Caricamento indicatori...</div>;
  if (!data) return <div className="py-12 text-center text-muted-foreground">Nessun dato disponibile per questo periodo. Carica un bilancio per vedere gli indicatori.</div>;

  const { indicators, trend, commitments_summary, alerts } = data;
  const categories = {};
  indicators.forEach(i => {
    if (!categories[i.category]) categories[i.category] = [];
    categories[i.category].push(i);
  });

  const trendData = trend.map(t => ({ name: `${MONTHS_IT[t.month]} ${t.year}`, ...t }));

  // Category icons and descriptions
  const categoryMeta = {
    'Quanto ti resta in tasca': { desc: 'Questi numeri ti dicono se la tua azienda guadagna abbastanza.' },
    'Dove vanno i tuoi soldi': { desc: 'Qui vedi come si dividono le tue spese e dove puoi intervenire.' },
    'Riesci a pagare i debiti?': { desc: 'La situazione finanziaria: rate, liquidita\' e copertura.' },
  };

  return (
    <div className="space-y-6" data-testid="kpi-dashboard">
      {/* Alert Summary */}
      {(alerts.critical > 0 || alerts.warning > 0) ? (
        <div className={`p-4 rounded-xl border-2 flex items-center gap-3 ${alerts.critical > 0 ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
          <AlertTriangle className={`h-6 w-6 ${alerts.critical > 0 ? 'text-red-600' : 'text-amber-600'}`} />
          <div>
            <div className="font-bold text-sm">
              {alerts.critical > 0 && <span className="text-red-700">{alerts.critical} {alerts.critical === 1 ? 'problema serio' : 'problemi seri'}</span>}
              {alerts.critical > 0 && alerts.warning > 0 && ' e '}
              {alerts.warning > 0 && <span className="text-amber-700">{alerts.warning} {alerts.warning === 1 ? 'cosa da monitorare' : 'cose da monitorare'}</span>}
            </div>
            <p className="text-xs text-slate-600 mt-0.5">Scorri per vedere i dettagli e le azioni consigliate.</p>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl border-2 bg-emerald-50 border-emerald-300 flex items-center gap-3">
          <CheckCircle className="h-6 w-6 text-emerald-600" />
          <div>
            <div className="font-bold text-sm text-emerald-700">La tua azienda sta bene</div>
            <p className="text-xs text-slate-600 mt-0.5">Non ci sono criticita' evidenti. Continua cosi'!</p>
          </div>
        </div>
      )}

      {/* Commitments Summary */}
      {commitments_summary.count > 0 && (
        <div className="flex gap-4 text-sm bg-slate-50 rounded-lg p-3 border">
          <span className="text-muted-foreground">Finanziamenti attivi: <span className="font-semibold text-foreground">{commitments_summary.count}</span></span>
          <span className="text-muted-foreground">Rate mensili: <span className="font-semibold text-foreground">
            {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(commitments_summary.monthly_total)}
          </span></span>
        </div>
      )}

      {/* Main Margin Evolution Chart */}
      {trendData.length > 1 && (
        <Card className="border-2">
          <CardHeader className="pb-1">
            <CardTitle className="text-base">Come vanno i tuoi margini nel tempo</CardTitle>
            <CardDescription>Quanto ti resta in tasca mese per mese (% sul fatturato)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-5 mb-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-600 inline-block rounded" /> Cassa prodotta (EBITDA)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blue-600 inline-block rounded" /> Guadagno operativo (ROS)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-violet-500 inline-block rounded border-dashed" style={{borderTop: '2px dashed #7c3aed', height: 0}} /> Margine dopo fornitori (MDC)</span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trendData} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" domain={['auto', 'auto']} />
                <Tooltip
                  formatter={(v, name) => {
                    const labels = { ebitda_pct: 'Cassa prodotta (EBITDA)', ros: 'Guadagno operativo (ROS)', mdc_pct: 'Margine dopo fornitori (MDC)' };
                    return [`${v}%`, labels[name] || name];
                  }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                />
                <Area type="monotone" dataKey="ebitda_pct" stroke="#059669" fill="url(#ebitda_grad)" strokeWidth={2.5} dot={{ r: 4, fill: '#059669' }}
                  label={{ position: 'top', fontSize: 10, fill: '#059669', formatter: (v) => `${v}%` }}
                />
                <Area type="monotone" dataKey="ros" stroke="#2563eb" fill="none" strokeWidth={2} dot={{ r: 4, fill: '#2563eb' }}
                  label={{ position: 'bottom', fontSize: 10, fill: '#2563eb', formatter: (v) => `${v}%` }}
                />
                <Area type="monotone" dataKey="mdc_pct" stroke="#7c3aed" fill="none" strokeWidth={1.5} dot={{ r: 3, fill: '#7c3aed' }} strokeDasharray="5 5" />
                <defs>
                  <linearGradient id="ebitda_grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards by Category */}
      {Object.entries(categories).map(([cat, inds]) => {
        const meta = categoryMeta[cat] || {};
        return (
          <div key={cat}>
            <div className="mb-3">
              <h3 className="text-base font-bold text-slate-800">{cat}</h3>
              {meta.desc && <p className="text-xs text-slate-500 mt-0.5">{meta.desc}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inds.map(ind => <IndicatorCard key={ind.id} indicator={ind} trendData={trend} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KPIDashboard;
